import { BCP47, formatAmount, formatPointsByLocale } from '@alk/numbers';
import {
    COL_DEF_COUNTER_AMOUNT,
    COL_DEF_COUNTER_CCY,
    COL_DEF_DEAL_NUMBER,
    COL_DEF_DEALT_AMOUNT,
    COL_DEF_DEALT_CCY,
    COL_DEF_FINAL_RATE,
    COL_DEF_FX_POINTS,
    COL_DEF_LEG_TYPE,
    COL_DEF_MATURITY_DATE,
    COL_DEF_PAIR,
    COL_DEF_SPOT_RATE,
} from './kondor-blotter.service.data';
import { ColDef, ValueGetterParams } from '@ag-grid-community/core';
import { getCurrenciesFromCurrencyPair } from '@alk/currency';
import { identity } from '@alk/core';

const HARDCODED_FRACTION_SIZE_PRICE = 5;

const sum = (sum: unknown, value: unknown) => Number(sum) + Number(value);
const formatPoint = formatPointsByLocale(navigator.language as BCP47);

export const BASE_AMOUNT = 'Base Amount';
export const SECONDARY_AMOUNT = 'Sec. Amount';

export const positionBlotterColumns = (): Array<ColDef> => [
    {
        ...COL_DEF_PAIR,
        rowGroup: true,
        hide: true,
        lockVisible: true,
    },
    {
        ...COL_DEF_MATURITY_DATE,
        rowGroup: true,
        hide: true,
        lockVisible: true,
    },
    {
        field: 'avgrate',
        headerName: 'Avg. FX Rate',
        type: [ 'rate' ],
        enableValue: true,
        minWidth: 100,
        width: 100,
        resizable: true,
        valueFormatter: (params) => {
            return formatPoint(HARDCODED_FRACTION_SIZE_PRICE)(params?.value ?? 0);
        },
        valueGetter: avgRateGetter,
    },
    {
        type: [ 'number' ],
        headerName: BASE_AMOUNT,
        field: BASE_AMOUNT,
        headerClass: 'standard-cell',
        resizable: true,
        minWidth: 120,
        width: 120,
        enableValue: true,
        aggFunc: (params) => {
            if (Boolean(params.values?.length)) {
                return params.values.reduce(sum, 0);
            }
            return undefined;
        },
        valueFormatter: params => formatAmount(params?.value ?? 0),
        valueGetter: baseAmountGetter,
    },
    {
        type: [ 'number' ],
        headerName: SECONDARY_AMOUNT,
        field: SECONDARY_AMOUNT,
        headerClass: 'standard-cell',
        resizable: true,
        minWidth: 120,
        width: 120,
        enableValue: true,
        aggFunc: (params) => {
            if (Boolean(params.values?.length)) {
                return params.values.reduce(sum, 0);
            }
            return undefined;
        },
        valueFormatter: params => formatAmount(params?.value ?? 0),
        valueGetter: secondaryAmountGetter,
    },
    COL_DEF_LEG_TYPE,
    COL_DEF_SPOT_RATE,
    COL_DEF_FX_POINTS,
    COL_DEF_FINAL_RATE,
    COL_DEF_DEAL_NUMBER,
];

export function baseAmountGetter(params: ValueGetterParams): number {
    if (Boolean(params.data)) {
        const baseCurrency = getCurrenciesFromCurrencyPair(params.data[ COL_DEF_PAIR.field ]).currencyBase;
        return baseCurrency === params.data[ COL_DEF_DEALT_CCY.field ] ?
            params.data[ COL_DEF_DEALT_AMOUNT.field ] :
            params.data[ COL_DEF_COUNTER_AMOUNT.field ];
    }
    return undefined;
}

export function secondaryAmountGetter(params: ValueGetterParams): number {
    if (Boolean(params.data)) {
        const quoteCurrency = getCurrenciesFromCurrencyPair(params.data[ COL_DEF_PAIR.field ]).currencyQuote;
        return quoteCurrency === params.data[ COL_DEF_COUNTER_CCY.field ] ?
            params.data[ COL_DEF_COUNTER_AMOUNT.field ] :
            params.data[ COL_DEF_DEALT_AMOUNT.field ];
    }
    return undefined;
}

export function avgRateGetter(params: ValueGetterParams): number {
    let baseAmount: number, secAmount = 0;

    if (Boolean(params.data)) {
        baseAmount = baseAmountGetter(params);
        secAmount = secondaryAmountGetter(params);
    } else if (params.node?.aggData) {
        baseAmount = params.node.aggData[ BASE_AMOUNT ];
        secAmount = params.node.aggData[ SECONDARY_AMOUNT ];
    }

    return [ baseAmount, secAmount ].every(identity) ? Math.abs(secAmount / baseAmount) : 0;
}
